import { useEffect, useMemo, useRef, useState } from "react"

import ReactECharts from "echarts-for-react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { Typography, useTheme, chartsTheme } from "@l2r-front/l2r-ui"
import { formatNumberWithUnit } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"

import * as Styled from "./RoadworksBudgetsKpi.styled"

export function RoadworksBudgetsKpiComponent(props) {

    const {
        currency,
        data,
    } = props

    const chartRef = useRef(null)
    const [chartWidth, setChartWidth] = useState(null)
    const theme = useTheme()
    const { t } = useTranslation(I18N_NAMESPACE)

    useEffect(() => {
        const resizeFunction = () => {
            if (chartRef.current) {
                chartRef.current.getEchartsInstance().resize()
                setChartWidth(chartRef.current.getEchartsInstance().getWidth())
            }
        }
        window.addEventListener("resize", resizeFunction)
        setTimeout(() => chartRef.current?.getEchartsInstance().resize(), 100)
        return () => {
            window.removeEventListener("resize", resizeFunction)
        }
    }, [])

    useEffect(() => {
        if (chartRef.current) {
            setChartWidth(chartRef.current.getEchartsInstance().getWidth())
        }
    }, [])

    const chartOptions = useMemo(() => {
        const dates = data.map(d => d.date)
        const done = data.map(d => d.done)
        const planned = data.map(d => d.planned)

        const currentYear = new Date().getFullYear()
        const centerIndex = data.reduce((closestIndex, item, i) => {
            const year = item.date
            const closestYear = data[closestIndex].date

            return Math.abs(year - currentYear) < Math.abs(closestYear - currentYear)
                ? i
                : closestIndex
        }, 0)
        const xCategoryCount = chartWidth ? chartWidth / 120 : 6
        const hasData = data.length > 0 && (done.length > 0 || planned.length > 0)

        return {
            grid: {
                top: 50,
                left: 70,
                right: 20,
                bottom: 20,
            },
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow",
                },
                padding: 10,
                renderMode: "richText",
                valueFormatter: (value) => `${formatNumberWithUnit(value)}${currency}`,
            },
            xAxis: [
                {
                    type: "category",
                    axisTick: {
                        alignWithLabel: true,
                    },
                    data: dates,
                },
            ],
            yAxis: [
                {
                    type: "value",
                    position: "left",
                    min: hasData ? null : 0,
                    max: hasData ? null : 10000,
                    alignTicks: true,
                    axisLine: {
                        show: true,
                    },
                    axisLabel: hasData ? {
                        formatter: (value) => `${formatNumberWithUnit(value)}`,
                        fontWeight: "bold",
                    } : null,
                },
            ],
            dataZoom: [
                {
                    type: "inside",
                    filterMode: "none",
                    startValue: centerIndex - Math.floor(xCategoryCount / 2),
                    endValue: centerIndex + Math.ceil(xCategoryCount / 2),
                    zoomLock: true,
                },
            ],
            series: [
                {
                    emphasis: {
                        "disabled": true,
                    },
                    name: t(I18N_NAMESPACE, "components.roadworksBudgetsKpi.legend.done"),
                    type: "bar",
                    data: done,
                    itemStyle: {
                        borderRadius: [2, 2, 0, 0],
                    },
                },
                {
                    emphasis: {
                        "disabled": true,
                    },
                    name: t(I18N_NAMESPACE, "components.roadworksBudgetsKpi.legend.planned"),
                    type: "bar",
                    data: planned,
                    itemStyle: {
                        borderRadius: [2, 2, 0, 0],
                    },
                },
            ],
        }
    }, [data, currency, chartWidth, t])

    return (
        <Styled.Wrapper>
            <ReactECharts
                option={chartOptions}
                opts={{ renderer: "svg" }}
                style={{ height: "auto", flex: "1 1 auto" }}
                theme={chartsTheme}
                ref={chartRef}
            />
            <Styled.LegendsWrapper>
                <Styled.LegendWrapper>
                    <Styled.Color color={theme.palette["primary"].main} />
                    <Typography>
                        {t(I18N_NAMESPACE, "components.roadworksBudgetsKpi.legend.done")}
                    </Typography>
                </Styled.LegendWrapper>
                <Styled.LegendWrapper>
                    <Styled.Color color={theme.palette["secondary"].light} />
                    <Typography>
                        {t(I18N_NAMESPACE, "components.roadworksBudgetsKpi.legend.planned")}
                    </Typography>
                </Styled.LegendWrapper>
            </Styled.LegendsWrapper>
        </Styled.Wrapper >
    )
}


RoadworksBudgetsKpiComponent.propTypes = {
    className: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({
        date: PropTypes.number.isRequired,
        done: PropTypes.oneOfType([PropTypes.number]),
        planned: PropTypes.oneOfType([PropTypes.number]),
    })).isRequired,
}